import React from 'react';
import { Alert, Box, Chip, CircularProgress, Link, Stack, Typography } from '@mui/material';
import { StyledSimpleCard } from 'styles';
import { getEnvVar } from 'utils/env';
import { useRealmMigrationSteps } from 'RealmModule/hooks/useRealmMigrations';
import { RealmMigrationStep } from 'RealmModule/services/realmMigrationApi';

const STEP_COLOR = (status: string | null) =>
  status === 'Failed' ? 'error' : status === 'Success' ? 'success' : 'info';

const GRAFANA_URL = 'https://grafana.prod.us-east4.mosaicapp.com';

/**
 * Tempo datasource per environment. Both live in the prod Grafana — party traces are a
 * separate datasource there, not a separate Grafana — so only the uid changes.
 */
const TEMPO_DATASOURCE_UID: Record<string, string> = {
  prod: 'af7vtaagkmz9cd',
  party: 'df5ib9awlz01sa',
};

/**
 * Grafana Explore deep link for one trace.
 *
 * Explore takes its whole state as a JSON blob in `left`, so this cannot be a plain path.
 * Returns null when the environment has no Tempo datasource rather than linking to a
 * broken Explore pane.
 */
const tempoTraceUrl = (traceId: string) => {
  const datasource = TEMPO_DATASOURCE_UID[(getEnvVar('UI_ENV') || '').toLowerCase()];
  if (!datasource) return null;

  const state = {
    datasource,
    queries: [{ refId: 'A', queryType: 'traceql', query: traceId }],
    range: { from: 'now-7d', to: 'now' },
  };
  return `${GRAFANA_URL}/explore?left=${encodeURIComponent(JSON.stringify(state))}`;
};

/** Trim the shared prefix so the lane reads as a phase, not a sentence. */
const stepLabel = (action: string) => action.replace('Realm Migration - ', '');

/**
 * The step tree of one migration, grouped into per-service lanes.
 *
 * Services migrate CONCURRENTLY, so a flat ordered list would misrepresent the timeline —
 * the `service` column is what splits it. Steps are keyed on row id because a resumed
 * migration appends a second set of children with the same action and service.
 */
export const MigrationStepsCard = ({
  migrationLogId,
  isRunning,
  tenant,
}: {
  migrationLogId: number | null;
  isRunning: boolean;
  /** Must match the tenant the list was read as — galaxy re-checks the row against it. */
  tenant?: string;
}) => {
  const { steps, isLoading, isError, error } = useRealmMigrationSteps(
    migrationLogId,
    isRunning,
    tenant,
  );

  if (migrationLogId === null) {
    return (
      <StyledSimpleCard>
        <h3>Migration Steps</h3>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          Select a migration above to see its steps.
        </Typography>
      </StyledSimpleCard>
    );
  }

  const orchestration = steps.filter((s) => !s.service);
  const byService = steps.reduce<Record<string, RealmMigrationStep[]>>((acc, step) => {
    if (!step.service) return acc;
    acc[step.service] = [...(acc[step.service] ?? []), step];
    return acc;
  }, {});

  return (
    <StyledSimpleCard>
      <h3>Migration Steps</h3>

      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 6,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}

      {isError && (
        <Alert severity="error">
          Could not load steps: {(error as any)?.data?.message ?? 'Unknown error'}
        </Alert>
      )}

      {!isLoading && !isError && (
        <Stack
          sx={{
            gap: 3,
          }}
        >
          <StepLane title="Orchestration" steps={orchestration} />
          {Object.entries(byService).map(([service, serviceSteps]) => (
            <StepLane key={service} title={service} steps={serviceSteps} />
          ))}

          <Alert severity="info">
            A green <strong>Create/Load Dump</strong> is not proof the dump succeeded — the job pod
            exits 0 even when the script fails. <strong>Verify Checksums</strong> is the step that
            actually gates data integrity.
          </Alert>
        </Stack>
      )}
    </StyledSimpleCard>
  );
};

const StepLane = ({ title, steps }: { title: string; steps: RealmMigrationStep[] }) => {
  if (steps.length === 0) return null;

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      <Stack
        sx={{
          gap: 1,
        }}
      >
        {steps.map((step) => (
          <Box
            key={step.id}
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <Chip size="small" label={step.status ?? 'unknown'} color={STEP_COLOR(step.status)} />
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
              {stepLabel(step.action)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {step.durationSeconds !== null ? `${step.durationSeconds}s` : '—'}
            </Typography>
            {!!step.traceId && !!tempoTraceUrl(step.traceId) && (
              <Link
                href={tempoTraceUrl(step.traceId) as string}
                target="_blank"
                rel="noopener noreferrer"
                variant="caption"
              >
                trace
              </Link>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default MigrationStepsCard;
